import { audio } from '../game/AudioManager.js';
import { gameState } from '../game/GameState.js';
import { questManager } from '../game/QuestManager.js';

export class WindowManager {
  constructor(container) {
    this.container = container;
    this.windows = new Map();
    this.topZ = 100;
  }

  createWindow(id, title, width, height, contentHtml) {
    if (this.windows.has(id)) {
      this.bringToFront(id);
      return this.windows.get(id);
    }

    const containerW = this.container.clientWidth || window.innerWidth;
    const containerH = this.container.clientHeight || window.innerHeight;
    const actualW = Math.min(width, containerW - 16);
    const actualH = Math.min(height, containerH - 46);

    const win = document.createElement('div');
    win.className = 'win-window';
    win.id = `win-${id}`;
    win.style.width = `${actualW}px`;
    win.style.height = `${actualH}px`;
    win.style.left = `${Math.max(8, (containerW - actualW) / 2)}px`;
    win.style.top = `${Math.max(8, (containerH - actualH) / 2 - 20)}px`;
    win.style.zIndex = ++this.topZ;

    win.innerHTML = `
      <div class="win-titlebar">
        <div class="win-title"><span>🗔</span> ${title}</div>
        <div class="win-controls">
          <button class="win-ctrl-btn minimize">_</button>
          <button class="win-ctrl-btn maximize">□</button>
          <button class="win-ctrl-btn close">✕</button>
        </div>
      </div>
      <div class="win-body">${contentHtml}</div>
    `;

    this.container.appendChild(win);
    this.windows.set(id, win);

    // Mouse & Touch Dragging logic
    const titlebar = win.querySelector('.win-titlebar');
    let isDragging = false;
    let startX = 0, startY = 0, initialLeft = 0, initialTop = 0;

    const startDrag = (clientX, clientY, target) => {
      if (target.closest('.win-controls')) return false;
      isDragging = true;
      this.bringToFront(id);
      startX = clientX;
      startY = clientY;
      initialLeft = parseInt(win.style.left, 10) || 0;
      initialTop = parseInt(win.style.top, 10) || 0;
      return true;
    };

    const moveDrag = (clientX, clientY) => {
      if (!isDragging) return;
      const newL = Math.max(0, Math.min(containerW - 60, initialLeft + (clientX - startX)));
      const newT = Math.max(0, Math.min(containerH - 40, initialTop + (clientY - startY)));
      win.style.left = `${newL}px`;
      win.style.top = `${newT}px`;
    };

    const endDrag = () => {
      isDragging = false;
    };

    titlebar.addEventListener('mousedown', (e) => {
      startDrag(e.clientX, e.clientY, e.target);
    });
    window.addEventListener('mousemove', (e) => {
      moveDrag(e.clientX, e.clientY);
    });
    window.addEventListener('mouseup', endDrag);

    // Touch dragging
    titlebar.addEventListener('touchstart', (e) => {
      if (e.touches.length === 1) {
        if (startDrag(e.touches[0].clientX, e.touches[0].clientY, e.target)) {
          e.preventDefault();
        }
      }
    }, { passive: false });

    window.addEventListener('touchmove', (e) => {
      if (isDragging && e.touches.length === 1) {
        moveDrag(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: true });

    window.addEventListener('touchend', endDrag);

    win.addEventListener('mousedown', () => this.bringToFront(id));

    win.querySelector('.close').addEventListener('click', () => {
      this.closeWindow(id);
    });

    return win;
  }

  bringToFront(id) {
    const win = this.windows.get(id);
    if (win) {
      win.style.zIndex = ++this.topZ;
    }
  }

  closeWindow(id) {
    const win = this.windows.get(id);
    if (win) {
      win.remove();
      this.windows.delete(id);
    }
  }
}
